import { EditOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons';
import {
  Button,
  Input,
  Table,
  TableColumnsType,
  Tooltip,
  message,
  Modal,
  ModalProps,
  Typography,
  Form,
  FormInstance,
} from 'antd';
import useModal from 'hooks/useModal';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SortEnd, SortableContainerProps } from 'react-sortable-hoc';
import { DragHandle, DraggableContainer, SortableItem } from 'components/DragTableComponent';
import {
  deleteUserHangupSubStatus,
  getUserHangupSubStatus,
  postUserHangupSubStatus,
  sortUserHangupSubStatus,
} from 'services/api/admin';
import StatusButton, { StatusButtonProps } from 'components/StatusButton';
import { useMemoizedFn, useRafState, useRequest } from 'ahooks';
import { useTranslate } from '@tolgee/react';
import { arrayMoveImmutable } from 'array-move';

interface UserHangupSubStatus {
  status: string;
  operation?: 'edit' | 'add';
}

interface HangupSettingsProps extends ModalProps {
  hidePopover: () => void;
  onHangupStatusListChange?: (list: string[]) => void;
}

interface OperationCellProps {
  dataIndex: number;
  save: (values: any) => Promise<undefined>;
  onDelete: StatusButtonProps['onClick'];
  onCancel: (dataIndex: number, operation?: 'edit' | 'add') => void;
}

const EditableContext = React.createContext<{
  form: FormInstance<any>;
  disabled: boolean;
  operation?: 'edit' | 'add';
  setDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  setOperation: React.Dispatch<React.SetStateAction<'edit' | 'add' | undefined>>;
} | null>(null);

const MAX_VALUES = 50;

const StatusCell = (
  props: UserHangupSubStatus & { dataIndex: number; onClick: (index: number) => void },
) => {
  const { status, dataIndex } = props;
  const { form, operation, setOperation, setDisabled } = useContext(EditableContext)!;
  const inputRef = useRef<Input>(null);
  const { t } = useTranslate();

  useEffect(() => {
    if (operation && inputRef) {
      inputRef.current?.focus();
    }
  }, [operation]);

  if (operation === 'add' || operation === 'edit') {
    return (
      <Form.Item className="!mb-0" name={dataIndex}>
        <Input ref={inputRef} showCount={true} maxLength={30} placeholder={t('trade.请输入')} />
      </Form.Item>
    );
  }

  return (
    <div className="whitespace-nowrap">
      <Typography.Paragraph
        className="!mb-0 inline-block max-w-[140px]"
        ellipsis={{ tooltip: true }}
      >
        {status}
      </Typography.Paragraph>
      <EditOutlined
        className="ml-1 cursor-pointer"
        onClick={() => {
          form.setFieldsValue({
            [dataIndex]: status,
          });
          setDisabled(false);
          setOperation('edit');
        }}
      />
    </div>
  );
};

const OperationCell = (props: OperationCellProps) => {
  const { dataIndex, save, onDelete, onCancel } = props;
  const { t } = useTranslate();
  const [loading, setLoading] = useState(false);

  const { form, disabled, operation, setOperation } = useContext(EditableContext)!;

  const handleSave = useCallback(() => {
    setLoading(true);
    save(form.getFieldValue(dataIndex)).finally(() => {
      setLoading(false);
    });
  }, [save, form, dataIndex]);

  return operation === 'edit' || operation === 'add' ? (
    <>
      <Button type="link" disabled={disabled} loading={loading} onClick={handleSave}>
        {t('translation.保存')}
      </Button>
      <Button
        type="link"
        onClick={() => {
          setOperation(undefined);
          onCancel(dataIndex, operation);
        }}
      >
        {t('translation.取消')}
      </Button>
    </>
  ) : (
    <StatusButton
      popConfirmProps={{
        title: t('hangup_settings_delete_confirm'),
      }}
      type="link"
      onClick={onDelete}
    >
      {t('marketing.删除')}
    </StatusButton>
  );
};

const HangupSettingsModal = (props: HangupSettingsProps) => {
  const { visible, onHangupStatusListChange, ...othersProps } = props;
  const [tableData, setTableData] = useRafState<UserHangupSubStatus[]>([]);
  const { t } = useTranslate();

  const { data: hangupSubStatus } = useRequest(() => getUserHangupSubStatus());

  const onSortEnd = useMemoizedFn(({ oldIndex, newIndex }: SortEnd) => {
    const sortedData = arrayMoveImmutable(tableData.slice(), oldIndex, newIndex);
    sortUserHangupSubStatus({
      states: sortedData.map((d) => d.status),
    })
      .then(() => {
        setTableData(sortedData);
      })
      .catch(() => {
        message.error(t('translation.操作失败，请稍后再试'));
      });
  });

  const Container = (props: SortableContainerProps) => (
    <DraggableContainer onSortEnd={onSortEnd} {...props} />
  );

  const DraggableBodyRow: React.FC<any> = ({ className, style, ...restProps }) => {
    const [form] = Form.useForm();
    // function findIndex base on Table rowKey props and should always be a right array index
    const index = tableData.findIndex((x) => x.status === restProps['data-row-key']);

    const [disabled, setDisabled] = useState(index > -1 ? !tableData[index]?.status : false);
    const [operation, setOperation] = useState<'edit' | 'add' | undefined>(
      tableData?.[index]?.operation,
    );

    return (
      <Form
        form={form}
        component={false}
        onValuesChange={(_, values) => {
          const v = Object.values(values);
          setDisabled(!v?.[0]);
        }}
      >
        <EditableContext.Provider
          value={{
            form,
            disabled,
            operation,
            setOperation,
            setDisabled,
          }}
        >
          <SortableItem index={index} {...restProps} />
        </EditableContext.Provider>
      </Form>
    );
  };

  const emitDataChange = useMemoizedFn((data: UserHangupSubStatus[]) => {
    onHangupStatusListChange?.(data.map((v) => v.status));
  });

  const handleStatusCellClick = useMemoizedFn((index: number) => {
    tableData[index].operation = 'edit';
    setTableData([...tableData]);
  });

  const columns: TableColumnsType<UserHangupSubStatus> = useMemo(
    () => [
      {
        title: '',
        dataIndex: 'sort',
        width: 30,
        className: 'drag-visible',
        render: () => <DragHandle />,
      },
      {
        title: t('field.index'),
        width: 40,
        className: 'drag-visible',
        render: (_, __, index) => {
          return index + 1;
        },
      },
      {
        key: 'status',
        title: t('aide.状态'),
        width: 120,
        dataIndex: 'status',
        className: 'drag-visible',
        render: (_, record, index) => {
          const { status, operation } = record;
          return (
            <StatusCell
              dataIndex={index}
              status={status}
              operation={operation}
              onClick={handleStatusCellClick}
            />
          );
        },
      },
      {
        title: t('操作'),
        width: 120,
        render: (_, record, index) => {
          const { status } = record;
          return (
            <OperationCell
              dataIndex={index}
              save={async (inputValue: string) => {
                if (tableData.some((value, i) => i !== index && value.status === inputValue)) {
                  message.error(t('hangup_settings_add_conflit'));
                  return;
                }
                try {
                  await postUserHangupSubStatus({
                    newState: inputValue,
                    originalState: status ?? '',
                  });
                  message.success(t('operation.saveSucceed'));
                  tableData[index] = {
                    status: inputValue,
                  };
                  setTableData([...tableData]);
                  emitDataChange(tableData);
                } catch (err) {
                  message.error(t('operation.saveFailed'));
                }
              }}
              onDelete={async () => {
                try {
                  await deleteUserHangupSubStatus({ originalState: status });
                  const newList = tableData.filter((d) => d.status !== status);
                  setTableData(newList);
                  emitDataChange(newList);
                  message.success(t('phrase.删除成功'));
                } catch (err) {
                  message.error(t('phrase.删除失败，请稍后再试'));
                }
              }}
              onCancel={(dataIndex, operation) => {
                if (operation === 'add') {
                  setTableData(tableData.filter((_, i) => i !== dataIndex));
                }
              }}
            />
          );
        },
      },
    ],
    [tableData, emitDataChange, setTableData, t, handleStatusCellClick],
  );

  const handleAdd = useCallback(() => {
    setTableData([
      ...tableData,
      {
        status: '',
        operation: 'add',
      },
    ]);
  }, [tableData, setTableData]);

  const addBtnDisabled = useMemo(() => tableData.length >= MAX_VALUES, [tableData]);

  useEffect(() => {
    if (visible && hangupSubStatus) {
      setTableData(
        hangupSubStatus.map((status) => ({
          status,
        })),
      );
    }
  }, [visible, hangupSubStatus, setTableData]);

  return (
    <Modal
      width={600}
      title={t('hangup_settings_modal_title')}
      footer={null}
      visible={visible}
      {...othersProps}
    >
      <>
        <div className="bg-bg-light text-content-300 px-3 py-2 rounded mb-4">
          {t('hangup_settings_modal_tips')}
        </div>
        <Table
          rowKey="status"
          pagination={false}
          dataSource={tableData}
          columns={columns}
          components={{
            body: {
              wrapper: Container,
              row: DraggableBodyRow,
            },
          }}
          scroll={{
            y: 400,
          }}
        ></Table>
        <Tooltip
          title={addBtnDisabled ? t('hangup_settings_add_max_msg', { num: MAX_VALUES }) : undefined}
        >
          <Button
            style={{ width: '100%' }}
            className="!w-full !mt-2"
            type="dashed"
            disabled={addBtnDisabled}
            onClick={handleAdd}
          >
            <PlusOutlined />
            {t('hangup_settings_modal_add_btn')}
          </Button>
        </Tooltip>
      </>
    </Modal>
  );
};

export const HangupSettings = (props: HangupSettingsProps) => {
  const { hidePopover } = props;
  const { visible, show, hide } = useModal(false);
  const { t } = useTranslate();

  const handleClick = useCallback(() => {
    hidePopover();
    show();
  }, [hidePopover, show]);

  return (
    <>
      <Tooltip title={t('hangup_settings_modal_title')} placement="top">
        <SettingOutlined onClick={handleClick} />
      </Tooltip>
      {visible && <HangupSettingsModal {...props} visible={visible} onCancel={hide} />}
    </>
  );
};
